import type {
  BusFilterType,
  GroupResponsibleState,
} from '@/scopes/account/group-responsible/domain/types'
import { getBusNumber } from '../utils/busUtils'
import { sortParticipants } from './utils'

export function getTransportType(
  participantId: string,
  buses: GroupResponsibleState['buses'],
  participant: GroupResponsibleState['participants'][string],
): 'bus' | 'ownTransport' | 'unknown' {
  const hasBusService = Object.values(buses).some(bus =>
    bus.participantsId.includes(Number(participantId)),
  )

  if (hasBusService) {
    return 'bus'
  }

  if (participant.isRegistered) {
    return 'ownTransport'
  }

  return 'unknown'
}

export function getParticipantsWithBusInfo(state: GroupResponsibleState) {
  const { participants, buses } = state
  if (!participants)
    return []

  const formatWithCode = (name?: string, code?: string) => {
    if (!name)
      return ''
    return code ? `${name} [${code}]` : name
  }

  return Object.entries(participants).map(([participantId, participant]) => {
    const associatedBuses = Object.values(buses).filter(bus => bus.participantsId.includes(Number(participantId)))
    const transportType = getTransportType(participantId, buses, participant)

    const outboundBus = associatedBuses.find(bus => bus.direction === 'Outbound')
    const inboundBus = associatedBuses.find(bus => bus.direction === 'Inbound')
    const locationParts = [
      formatWithCode(outboundBus?.departureLocation, outboundBus?.departureLocationCode),
      formatWithCode(inboundBus?.arrivalLocation, inboundBus?.arrivalLocationCode),
    ].filter(Boolean)

    const location = locationParts.length ? locationParts.join(' - ') : '-'
    const busNumber = `${getBusNumber(outboundBus)}/${getBusNumber(inboundBus)}`
    const label = (() => {
      if (associatedBuses.length) {
        return {
          state: 'success',
          text: 'groups_responsible_status_booked',
        }
      }

      if (participant.isRegistered) {
        return {
          state: 'info',
          text: 'groups_responsible_status_not_applicable',
        }
      }

      return {
        state: 'error',
        text: 'groups_responsible_status_not_booked',
      }
    })()

    const participantData = {
      id: participantId,
      name: participant.name,
      email: participant.email,
      isRegistered: participant.isRegistered,
      isBusAssignmentProcessing: participant.isBusAssignmentProcessing,
      transportType,
      busInfo: associatedBuses.length
        ? {
            number: busNumber,
            location,
          }
        : null,
      label,
    }

    return participantData
  })
}

export function getParticipantsBusBookedCounts(state: GroupResponsibleState): {
  all: number
  notBooked: number
  booked: number
} {
  const participantsWithBusInfo = getParticipantsWithBusInfo(state)

  const busParticipants = participantsWithBusInfo.filter(p =>
    p.transportType === 'bus',
  )

  const all = busParticipants.length
  const notBooked = busParticipants.filter(p => p.label.text === 'groups_responsible_status_not_booked').length
  const booked = busParticipants.filter(p => p.label.text === 'groups_responsible_status_booked').length

  return { all, notBooked, booked }
}

export function getFilteredParticipantsBusBooked(state: GroupResponsibleState, filter: BusFilterType, searchTerm = '') {
  const participantsWithBusInfo = getParticipantsWithBusInfo(state)
  const trimmedSearchTerm = searchTerm.trim().toLowerCase()

  const filteredParticipants = participantsWithBusInfo.filter((participant) => {
    const isBusTransport = participant.transportType === 'bus'
    const matchesFilter = filter === 'all' || participant.label.text === `groups_responsible_status_${filter}`
    const matchesSearch = trimmedSearchTerm === '' || participant.name.toLowerCase().includes(trimmedSearchTerm)
      || participant.busInfo?.location.toLowerCase().includes(trimmedSearchTerm)
      || participant.busInfo?.number.toLowerCase().includes(trimmedSearchTerm)

    return isBusTransport && matchesFilter && matchesSearch
  })

  return filteredParticipants.sort(sortParticipants)
}

export function getParticipantsEditingData(state: GroupResponsibleState) {
  const selectedParticipantIds = state.selectedParticipantIds
  const participants = state.participants || {}
  const buses = state.buses || {}

  const busParticipantsCount = Object.entries(participants).filter(([participantId, participant]) => {
    const transportType = getTransportType(participantId, buses, participant)
    return transportType === 'bus'
  }).length

  const selectedCount = selectedParticipantIds.size
  const totalCount = busParticipantsCount

  return {
    selectedCount,
    totalCount,
  }
}

export function getHasProcessingParticipants(state: GroupResponsibleState): boolean {
  const { selectedParticipantIds, participants } = state

  if (!participants || selectedParticipantIds.size === 0) {
    return false
  }

  const selectedParticipants = Array.from(selectedParticipantIds)
    .map(id => participants[id])
    .filter(Boolean)

  const hasProcessingParticipants = selectedParticipants.some(
    participant => participant.isBusAssignmentProcessing,
  )

  return hasProcessingParticipants
}
